
import './index.css';

import React, { useEffect, useState } from 'react';

import StatusBadge from '../../ui/StatusBadge';
import RecordSection from '../RecordSection';
import SubRecordModal from '../../ui/SubRecordModal';
import Factory from '../../../records/modules';

const HEALTH_OPTIONS = [
  { value: 'green', label: 'Green' },
  { value: 'amber', label: 'Amber' },
  { value: 'red', label: 'Red' },
];

const EMPTY_WEEKLY = { weekStart: '', health: 'green', summary: '', blockers: '', nextSteps: '' };

function WeeklyUpdates({ meta, domain, parentId }) {
  const [weeklyUpdates, setWeeklyUpdates] = useState(domain.weeklyUpdates || []);
  const [weeklyModal, setWeeklyModal] = useState({ open: false, editing: null, form: EMPTY_WEEKLY, submitting: false, error: '' });

  useEffect(() => {
    setWeeklyUpdates(domain.weeklyUpdates || []);
  }, [domain.weeklyUpdates]);

  function openAddWeekly() {
    setWeeklyModal({ open: true, editing: null, form: EMPTY_WEEKLY, submitting: false, error: '' });
  }

  function openEditWeekly(row) {
    setWeeklyModal({
      open: true,
      editing: row,
      form: {
        weekStart: row.weekStart || '',
        health: row.health || 'green',
        summary: row.summary || '',
        blockers: row.blockers || '',
        nextSteps: row.nextSteps || '',
      },
      submitting: false,
      error: '',
    });
  }

  function closeWeeklyModal() {
    setWeeklyModal((s) => ({ ...s, open: false }));
  }

  function setWeeklyField(field, value) {
    setWeeklyModal((s) => ({ ...s, form: { ...s.form, [field]: value } }));
  }

  async function submitWeekly() {
    setWeeklyModal((s) => ({ ...s, submitting: true, error: '' }));
    try {
      const payload = {
        weekStart: weeklyModal.form.weekStart,
        health: weeklyModal.form.health,
        summary: weeklyModal.form.summary.trim(),
        blockers: weeklyModal.form.blockers.trim() || null,
        nextSteps: weeklyModal.form.nextSteps.trim() || null,
      };

      if (weeklyModal.editing) {
        const updated = await Factory({ slug: meta.slug }).Data.updateRecord(weeklyModal.editing.id, payload);
        setWeeklyUpdates((current) => current.map((item) => (
          item.data.id === updated.id ? { data: updated } : item
        )));
      } else {
        const created = await Factory({ slug: meta.slug }).Data.createRecord({
          ...payload,
          [meta.parentId]: parentId,
        });
        setWeeklyUpdates((current) => [...current, { data: created }]);
      }

      closeWeeklyModal();
    } catch (err) {
      const message = err?.response?.data?.message || 'Unable to save weekly update.';
      setWeeklyModal((s) => ({ ...s, submitting: false, error: message }));
    }
  }

  async function handleDeleteWeekly(row) {
    if (!window.confirm(`Delete the weekly update for "${row.weekStart}"? This cannot be undone.`)) return;
    try {
      await Factory({ slug: meta.slug }).Data.deleteRecord(row.id);
      setWeeklyUpdates((current) => current.filter((item) => item.data.id !== row.id));
    } catch (err) {
      alert(err?.response?.data?.message || 'Unable to delete weekly update.');
    }
  }

  return (
    <>
      <RecordSection
        title="Weekly Updates"
        className="record-weekly"
        actions={(
          <button className="button-secondary" onClick={openAddWeekly} type="button">
            + Add Weekly Update
          </button>
        )}
      >
        {weeklyUpdates.length === 0 ? (
          <p>No weekly updates recorded yet.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Week Start</th>
                <th>Health</th>
                <th>Summary</th>
                <th>Blockers</th>
                <th>Next Steps</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {weeklyUpdates.map((row) => (
                <tr key={row.data.id}>
                  <td>{row.data.weekStart}</td>
                  <td><StatusBadge value={row.data.health} /></td>
                  <td>{row.data.summary}</td>
                  <td>{row.data.blockers || '-'}</td>
                  <td>{row.data.nextSteps || '-'}</td>
                  <td>
                    <div className="sub-record-row-actions">
                      <button className="button-row-edit" onClick={() => openEditWeekly(row.data)} type="button">Edit</button>
                      <button className="button-row-delete" onClick={() => handleDeleteWeekly(row.data)} type="button">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </RecordSection>

      <SubRecordModal
        error={weeklyModal.error}
        isOpen={weeklyModal.open}
        isSubmitting={weeklyModal.submitting}
        onClose={closeWeeklyModal}
        onSubmit={submitWeekly}
        submitLabel={weeklyModal.editing ? 'Save Changes' : 'Add Weekly Update'}
        title={weeklyModal.editing ? 'Edit Weekly Update' : 'Add Weekly Update'}
      >
        <div className="form-grid">
          <div className="form-grid two-col">
            <label>
              Week Start <span aria-hidden="true">*</span>
              <input
                autoFocus
                onChange={(e) => setWeeklyField('weekStart', e.target.value)}
                required
                type="date"
                value={weeklyModal.form.weekStart}
              />
            </label>
            <label>
              Health
              <select onChange={(e) => setWeeklyField('health', e.target.value)} value={weeklyModal.form.health}>
                {HEALTH_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
          </div>
          <label>
            Summary <span aria-hidden="true">*</span>
            <textarea
              onChange={(e) => setWeeklyField('summary', e.target.value)}
              placeholder="What happened this week?"
              required
              value={weeklyModal.form.summary}
            />
          </label>
          <label>
            Blockers
            <textarea
              onChange={(e) => setWeeklyField('blockers', e.target.value)}
              placeholder="Any impediments or blockers?"
              value={weeklyModal.form.blockers}
            />
          </label>
          <label>
            Next Steps
            <textarea
              onChange={(e) => setWeeklyField('nextSteps', e.target.value)}
              placeholder="What's planned for next week?"
              value={weeklyModal.form.nextSteps}
            />
          </label>
        </div>
      </SubRecordModal>
    </>
  );
}

export default WeeklyUpdates;